import React, { useEffect, useState } from 'react';

import { Platform } from 'react-native';

import { Box } from 'components/ui';

type AccessibilityLiveRegion = 'none' | 'polite' | 'assertive';

const mapLiveRegionToAriaLive = (
  liveRegion: AccessibilityLiveRegion
): 'polite' | 'assertive' | 'off' => {
  if (liveRegion === 'none') {
    return 'off';
  }
  return liveRegion;
};

/**
 * PageAnnouncer is an accessibility utility component that announces the page name
 * to assistive technologies (e.g., screen readers).
 *
 * This component updates an invisible `View` with an `accessibilityLabel` and
 * `aria-live="polite"` to ensure that screen readers notify users, without interrupting
 * ongoing speech.
 */
export const PageAnnouncer: React.FC<React.PropsWithChildren<{
  pageTitle: string;
  accessibilityLiveRegion?: AccessibilityLiveRegion;
}>> = ({ pageTitle, accessibilityLiveRegion = 'polite' }) => {
  const [currentPage, setCurrentPage] = useState('');

  useEffect(() => {
    setCurrentPage(pageTitle);
  }, [pageTitle]);

  return (
    <Box
      accessible={false}
      accessibilityLabel={currentPage}
      testID="page-announcer"
      accessibilityLiveRegion={accessibilityLiveRegion}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      aria-hidden={true}
      {...(Platform.OS === 'web' && {
        'aria-live': mapLiveRegionToAriaLive(accessibilityLiveRegion),
        role: 'status',
      })}
      style={{
        width: 0,
        height: 0,
        opacity: 0,
      }}
    />
  );
};
